import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../../lib/api-client';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

interface MerchantItem {
  id: string;
  uuid: string;
  code: string;
  name: string;
  paymentChannelPoolName: string;
  bankCode: string;
  accountNo: string;
  accountName: string;
  status: string;
  syncedAt: string | null;
  lastStatSyncAt: string | null;
}

export function MerchantList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['seller', 'merchants'],
    queryFn: () => apiGet<{ items: MerchantItem[]; total: number }>('/seller/merchants'),
  });

  const syncMutation = useMutation({
    mutationFn: () =>
      apiPost<{ added: number; updated: number; unchanged: number; errors: string[] }>(
        '/seller/sync/merchants',
      ),
    onSuccess: (res) => {
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ['seller', 'merchants'] });
      } else {
        setError(res.error.message);
      }
    },
  });

  const merchants = data?.ok ? data.data.items : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Button onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending} size="sm">
          {syncMutation.isPending ? t('common.loading') : t('merchants.syncMerchants')}
        </Button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-600 dark:bg-red-950 dark:text-red-300">
          {error}
        </div>
      )}

      {syncMutation.isSuccess && syncMutation.data?.ok && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300">
          {t('merchants.syncSummary', {
            added: syncMutation.data.data.added,
            updated: syncMutation.data.data.updated,
            unchanged: syncMutation.data.data.unchanged,
          })}
          {syncMutation.data.data.errors.length > 0 &&
            ` (${syncMutation.data.data.errors.length} errors)`}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading && <div className="p-6 text-gray-400">{t('common.loading')}</div>}
          {!isLoading && merchants.length === 0 && (
            <div className="p-6 text-gray-400">{t('merchants.emptyHint')}</div>
          )}
          {merchants.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('merchants.code')}</TableHead>
                    <TableHead>{t('merchants.name')}</TableHead>
                    <TableHead>{t('merchants.paymentChannel')}</TableHead>
                    <TableHead>{t('merchants.accountNo')}</TableHead>
                    <TableHead>{t('merchants.status')}</TableHead>
                    <TableHead>{t('merchants.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {merchants.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="font-mono text-sm">{m.code}</TableCell>
                      <TableCell>{m.name}</TableCell>
                      <TableCell className="text-sm text-gray-600 dark:text-gray-300">
                        {m.paymentChannelPoolName || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600 dark:text-gray-300">
                        {m.accountNo ? `${m.bankCode} ${m.accountNo}` : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            m.status === 'active'
                              ? 'success'
                              : m.status === 'locked'
                                ? 'danger'
                                : 'default'
                          }
                        >
                          {t(`common.${m.status}`)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/seller/merchants/${m.id}/stats`)}
                        >
                          {t('merchants.viewStats')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
